import { Dictionary, isArray, isUndefined, omitBy, pickBy } from "lodash";
import { EstabrookContext } from "./estabrookContext";

// @ts-ignore
const baseUrl = import.meta.env.VITE_BASE_BACKEND_URL;

export type ErrorWrapper<TError> =
  | TError
  | {
      statusCode: "unknown";
      errors: { code: string; detail: string }[];
      requestId?: string;
    };

export type EstabrookFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams,
> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
} & EstabrookContext["fetcherOptions"];

export async function estabrookFetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
  refreshToken,
}: EstabrookFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  try {
    if (refreshToken) {
      const tokens = await refreshToken();
      if (tokens && headers) {
        headers.authorization = `Bearer ${tokens.accessToken}`;
      }
    }

    const requestHeaders: HeadersInit = {
      "Content-Type": "application/json",
      ...headers,
    };

    /**
     * As the fetch API is being used, when multipart/form-data is specified
     * the Content-Type header must be deleted so that the browser can set
     * the correct boundary.
     * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
     */
    if (
      requestHeaders["Content-Type"]
        .toLowerCase()
        .includes("multipart/form-data")
    ) {
      delete requestHeaders["Content-Type"];
    }

    const response = await window.fetch(
      `${baseUrl}${resolveUrl(url, queryParams, pathParams)}`,
      {
        signal,
        method: method.toUpperCase(),
        body: body
          ? body instanceof FormData
            ? body
            : JSON.stringify(body)
          : undefined,
        headers: requestHeaders,
      },
    );
    if (!response.ok) {
      let error: ErrorWrapper<TError>;
      try {
        error = await response.json();
      } catch (e) {
        error = {
          statusCode: "unknown" as const,
          errors: [
            {
              code: "unknown",
              detail:
                e instanceof Error
                  ? `Unexpected error (${e.message})`
                  : "Unexpected error",
            },
          ],
        };
      }

      throw error;
    }

    if (response.headers.get("content-type")?.includes("json")) {
      return await response.json();
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      return (await response.blob()) as unknown as TData;
    }
  } catch (e) {
    /* console.error("thrown error", e);
    let errorObject: Error = {
      name: "unknown" as const,
      message:
        e instanceof Error ? `Network error (${e.message})` : "Network error",
      stack: e as string,
    }; */
    throw e;
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string | string[]> = {},
  pathParams: Record<string, string> = {},
) => {
  // we need to make sure we send the right format for array params
  const validParams = omitBy(queryParams, isUndefined);
  const validStringParams = omitBy(validParams, isArray);
  const validArayParams: Dictionary<string[]> = pickBy(validParams, isArray);
  const urlQuery = new URLSearchParams(
    validStringParams as Record<string, string>,
  );
  for (const [key, value] of Object.entries(validArayParams)) {
    value.forEach((v: string) => urlQuery.append(`${key}[]`, v));
  }
  let query = urlQuery.toString();
  if (query) query = `?${query}`;
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query;
};
